import * as Bowser from 'bowser';
import Log from "../Log";

/**
 * Removes the element from the dom that matches the corresponding id
 *
 * @param elementId - The selector corresponding to the elements we have to remove.
 */
export const removeDomElementById = (elementId: string): void =>  {
  const elementToRemove: Element | null = document.getElementById(elementId);
  if (elementToRemove) {
    const parentNode = elementToRemove.parentNode;
    if (parentNode) {
      parentNode.removeChild(elementToRemove);
    }
  }
}

/**
 * Adds an html string given in the params to the element that's represented
 * either directly by the target param or is found by searching the target selector.
 *
 * @param target - The element or its selector that will have the css class added
 * @param elementHtml - The html string that will be added as a child of target
 */
export const addDomElement = (target: string | Element, elementHtml: string): void => {
  const targetElement: Element | null  = typeof target === 'string'
    ? document.querySelector(target)
    : target;

  if (!targetElement) {
    Log.error(`${target} must be a CSS selector string or DOM element object`);
    return;
  }

  targetElement.insertAdjacentHTML('beforeend', elementHtml);
}

/**
 * Searches for the dom element represented by selectorId. If not found then
 * it creates a div element with the given id.
 *
 * @param selectorId
 */
export const findOrCreateElementById = (
  selectorId: string,
  elementType: keyof HTMLElementTagNameMap = 'div'
): Element => {
  const existingElement = document.querySelector(`${elementType}#${selectorId}`);

  if (!existingElement) {
    Log.debug(`No ${elementType} matching the id ${selectorId} was found. Creating the element.`);

    const newElement: Element = document.createElement(elementType)
    newElement.id = selectorId;
    return newElement
  }

  return existingElement;
}

/**
 * Adds the css class given in the params to the element that's represented
 * either directly by the target param or is found searching the target selector.
 *
 * @param target - The element or its selector that will have the css class added
 * @param cssClass
 */
export const addCssClass = (target: string | Element, cssClass: string): void => {
  const targetElement: Element | null  = typeof target === 'string'
    ? document.querySelector(target)
    : target;

  if (!targetElement) {
    Log.error(`${target} must be a CSS selector string or DOM element object`);
    return;
  }

  targetElement.classList.add(cssClass);
}

/**
 * Adds a style tag with the given css text.
 *
 * This method will create a style tag only once when the page loads,
 * and the actual call to the method will replace the content of the tags,
 * preventing multiple styles being appended to the document.
 */
export const addCssStyle = ((): ((cssText: string) => void) => {
  const style: HTMLStyleElement = document.createElement('style');
  style.type = 'text/css';
  const head = document.head;
  if (!head) {
    // Do nothing as we cannot find a head tag to attach the new styles
    return (): void => { /*  */};
  }

  head.append(style);
  return (cssText: string): void => { style.textContent = cssText; }
})();

/**
 * Checks the user agent string and determines if the user is on
 * a mobile device
 */
export const isMobile = (): boolean => {
  const browser = Bowser.getParser(window.navigator.userAgent);
  return browser.getPlatformType() === 'mobile';
}

/**
 * Adds a wait for the number of seconds specified
 *
 * @param seconds
 */
export const delay = async (seconds: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, seconds * 1000));
}
